// =============================================================================
// Legacy Hook Wrapper - useFavorites
// Maintient la compatibilité API avec l'ancien hook
// N'importe PAS supabase - utilise le store du module core
// =============================================================================

import { useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useFavoritesStore } from '@/modules/core/favorites';

// Re-export the type with old naming for backward compatibility
export interface Favorite {
  id: string;
  project_id: string;
  project_name: string;
}

export function useFavorites() {
  const { user } = useAuth();
  const { currentOrganization } = useOrganization();
  
  const {
    favorites: storeFavorites,
    loading,
    setContext,
    loadFavorites,
    addFavorite: storeAddFavorite,
    removeFavorite: storeRemoveFavorite,
    isFavorite: storeIsFavorite,
    toggleFavorite: storeToggleFavorite,
  } = useFavoritesStore();

  // Sync context when user/org changes
  useEffect(() => {
    setContext(user?.id || null, currentOrganization?.id || null);
  }, [user?.id, currentOrganization?.id, setContext]);

  // Load favorites when context is set
  useEffect(() => {
    if (user && currentOrganization) {
      loadFavorites();
    }
  }, [user, currentOrganization, loadFavorites]);

  // Map to old format (project_id instead of projectId)
  const favorites: Favorite[] = storeFavorites.map((f) => ({
    id: f.id,
    project_id: f.projectId,
    project_name: f.projectName,
  }));

  // Wrapper to match old API signature
  const addFavorite = useCallback(
    async (projectId: string, projectName: string) => {
      await storeAddFavorite(projectId, projectName);
    },
    [storeAddFavorite]
  );

  const removeFavorite = useCallback(
    async (projectId: string) => {
      await storeRemoveFavorite(projectId);
    },
    [storeRemoveFavorite]
  );

  const isFavorite = useCallback(
    (projectId: string) => {
      return storeIsFavorite(projectId);
    },
    [storeIsFavorite]
  );

  const toggleFavorite = useCallback(
    async (projectId: string, projectName: string) => {
      await storeToggleFavorite(projectId, projectName);
    },
    [storeToggleFavorite]
  );

  return {
    favorites,
    loading,
    addFavorite,
    removeFavorite,
    isFavorite,
    toggleFavorite,
    refresh: loadFavorites,
  };
}
